// src/components/TOSPdfViewerModal.tsx
import appSettings from "@/appsettings.json";
import { SessionContext } from "@/context/SessionContext";
import {
    Button,
    Group,
    Loader,
    Modal,
    Stack,
    Text,
    useMantineTheme,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import React, { useContext, useEffect, useState } from "react";


const TosPdfViewerModal: React.FC<{ opened: boolean; onClose: () => void }> = ({
    opened,
    onClose,
}) => {
    const theme = useMantineTheme();
    const session = useContext(SessionContext);
    if (!session) throw new Error("TOSPdfViewerModal must be used within SessionContext.");

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Build the PDF URL if currentTos is available
    const pdfUrl = session.currentTos
        ? `${appSettings.S3BucketBaseUrl}TOS/${session.currentTos.version}.pdf`
        : null;

    useEffect(() => {
        if (opened) {
            setLoading(true);
            setError(null);
        }
    }, [opened, pdfUrl]);

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            size="xl"
            title={`Terms of Service${session.currentTos ? ` (v${session.currentTos.version})` : ""}`}
            withCloseButton
            styles={{
                title: { fontSize: 20 },
                content: { paddingTop: 0 },
            }}
        >
            <Stack gap="sm">
                <div
                    style={{
                        height: "70vh",
                        borderRadius: 8,
                        overflow: "hidden",
                        border: `1px solid ${theme.colors.gray[3]}`,
                        background: theme.colors.gray[0],
                    }}
                >
                    {!pdfUrl ? (
                        <Stack align="center" justify="center" h="100%" p="md">
                            <IconAlertCircle color={theme.colors.red[6]} />
                            <Text ta="center">No current Terms of Service are available.</Text>
                        </Stack>
                    ) : error ? (
                        <Stack align="center" justify="center" h="100%" p="md">
                            <IconAlertCircle color={theme.colors.red[6]} />
                            <Text ta="center">{error}</Text>
                            <Button
                                component="a"
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="light"
                            >
                                Open PDF in new tab
                            </Button>
                        </Stack>
                    ) : (
                        <>
                            {loading && (
                                <Group justify="center" p="md">
                                    <Loader />
                                    <Text c="dimmed">Loading PDF…</Text>
                                </Group>
                            )}
                            <iframe
                                key={pdfUrl}
                                src={pdfUrl}
                                title="Terms of Service PDF"
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                    display: loading ? "none" : "block",
                                }}
                                onLoad={() => setLoading(false)}
                                onError={() => {
                                    setLoading(false);
                                    setError(
                                        "Unable to load the PDF in an embedded view. Your browser or settings may block it."
                                    );
                                }}
                            />
                        </>
                    )}
                </div>

                <Group justify="space-between">
                    <Text size="sm" c="dimmed">
                        {session.currentTos?.sha256
                            ? `Hash: ${session.currentTos.sha256}`
                            : "Hash: —"}
                    </Text>
                    <Group>
                        {pdfUrl && (
                            <Button
                                component="a"
                                href={pdfUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="subtle"
                            >
                                Open in new tab
                            </Button>
                        )}
                        <Button onClick={onClose}>Close</Button>
                    </Group>
                </Group>
            </Stack>
        </Modal>
    );
};

export default TosPdfViewerModal;
